import React from 'react';
import classnames from 'classnames';
import { filter } from 'helpers/filter';
import ClickOutside from 'components/ClickOutside';
import Scrollbars from 'components/Scrollbar';
import Icon from 'components/Icon';
import { Subtitle } from 'components/Text';
import { t } from 'components/Translate';
import SearchInput from 'components/Input/Search';

import searchStyles from 'components/Select/Select.module.scss';
import styles from './Dropdown.module.scss';

type Props<Item = { [key: string]: any }> = {
  items: Item[],
  selected: string | null,
  onChange: (item: Item, hide: () => void) => void,
  keyParam?: keyof Item,

  search?: {
    enabled: boolean,
    placeholder?: string,
    filterBy?: (item: Item) => string,
  },

  renderItem: (data: {
    key: string,
    item: Item,
    onClick: () => void,
  }) => React.ReactNode,
  renderSelected: (item: Item | null) => React.ReactNode,

  addText?: React.ReactChild,
  onAdd?: () => void,
  className?: string,
  height?: number,
  size?: 'normal' | 'large',
  isSelect?: boolean,
};
type State = {
  isActive: boolean,
  query: string,
};

class Dropdown<Item = { [key: string]: any }> extends React.PureComponent<Required<Props<Item>>, State> {
  static defaultProps: OptionalProps<Props> = {
    keyParam: 'id',
    addText: '',
    onAdd: undefined,
    search: {
      enabled: false,
    },
    height: 165,
    size: 'large',
    isSelect: false,
  };
  private search = React.createRef<{ focus: () => void }>();
  state: State = {
    isActive: false,
    query: '',
  };

  hide = () => {
    if (!this.state.isActive) return;

    this.setState({ isActive: false }, () => this.setState({ query: '' }));
  };
  toggle = () => this.setState({ isActive: !this.state.isActive, query: '' }, () => {
    if (this.search.current && this.state.isActive) this.search.current.focus();
  });

  private getItems(): Item[] {
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    const filterByMethod = (item: Item) => item[this.props.keyParam].toString();
    const filterBy = this.props.search.filterBy || filterByMethod;
    return filter(this.props.items, this.state.query, filterBy);
  }

  render() {
    const { keyParam, renderSelected, selected, className = '', size } = this.props;
    const items = this.getItems();
    const selectedItem = this.props.items.find((item) => (item[keyParam] as unknown) === selected) || null;
    const classes = classnames(styles.dropdown, styles[size], className, {
      [styles.is__active] : this.state.isActive,
      [styles.is__select] : this.props.isSelect,
    });

    return (
      <ClickOutside
        onClickOutside={ this.hide }
      >
        <div
          className={ classes }
          onClick={ this.toggle }
        >
          <div className={ styles.inner }>
            { renderSelected(selectedItem) }
            <div className={ styles.chevron }>
              <Icon.Inlined name="chevron-down" color="dark-light" />
            </div>
          </div>
          { this.renderContent(items) }
        </div>
      </ClickOutside>
    );
  }
  private renderContent(items: Item[]) {
    const { keyParam, renderItem, search, height } = this.props;
    const shouldRenderContent = items.length > 1 || !!this.state.query.length;

    return (
      <div className={ classnames(styles.select, { [styles.has__search] : search.enabled }) }>
        { this.renderSearch() }
        { shouldRenderContent &&
          <Scrollbars autoHeight={ true } autoHeightMax={ height }>
            <ul className={ styles.list }>
              {
                items.map((item) => renderItem({
                  key: item[keyParam] as unknown as string,
                  onClick: () => this.props.onChange(item, this.hide),
                  item,
                }))
              }
            </ul>
          </Scrollbars>
        }
        { this.renderAdd() }
      </div>
    );
  }
  private renderAdd() {
    if (!this.props.addText || !this.props.onAdd) return null;

    return (
      <div className={ classnames('hover_with_icon', styles.addNew) } onClick={ this.props.onAdd }>
        <Icon.Normal name="add" className="hovered__icon" />
        <Icon.Normal name="add-white" className="icon__white" />
        <Subtitle type="small">
          { t('common.action.addNew') }&nbsp;
          { this.props.addText }
        </Subtitle>
      </div>
    );
  }

  private renderSearch() {
    const { enabled, placeholder } = this.props.search;
    if (!enabled) return null;

    return (
      <div className={ searchStyles.search } onClick={ (e) => e.stopPropagation() }>
        <SearchInput
          ref={ this.search }
          query={ this.state.query }
          placeholder={ placeholder }
          onChange={ (query) => this.setState({ query }) }
        />
      </div>
    );
  }
}

export default Dropdown;
